import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import styles from './overview.css';
import { IFotoramaData } from '../../../../typings/fotorama';
import { PlayIcon } from '../../common/icons';
import { getYoutubeVideoPreviewUrl } from '../slider/youtube';

interface IGalleryOverviewProps {
  slides: IFotoramaData[];
  currentSlide: number;
  offsetX: number;
  visible?: boolean;
  setSlide(index: number): void;
}

export class GalleryOverview extends React.Component<IGalleryOverviewProps, object> {
  private overviewNode: HTMLDivElement;
  private slideNode: HTMLDivElement;

  public UNSAFE_componentWillReceiveProps(nextProps: IGalleryOverviewProps) {
    if (nextProps.offsetX !== this.props.offsetX && nextProps.offsetX !== 0) {
      this.scrollWithOffsetX(this.props.offsetX);
    }
  }

  public render() {
    const { slides, currentSlide } = this.props;

    const imgs = slides.map(({ thumb, video }, index) => {
      const isVideo = Boolean(video);
      const isCurrentSlide = currentSlide === index;

      return (
        <div
          {...mergeStyles(styles['image_wrapper'], isCurrentSlide && styles['active'])}
          key={video || thumb}
          ref={isCurrentSlide ? this.setSlideNode : undefined}
          onClick={this.changeSlide(index)}
        >
          {isVideo && (
            <div className={styles['play']}>
              <PlayIcon />
            </div>
          )}
          <img src={isVideo ? getYoutubeVideoPreviewUrl(video as string) : thumb} className={styles['image']} />
        </div>
      );
    });

    return (
      <div
        {...mergeStyles(styles['overview'], !this.props.visible && styles['overview_invisible'])}
        ref={this.setOverviewNode}
      >
        {imgs}
      </div>
    );
  }

  private changeSlide = (index: number) => () => {
    this.props.setSlide(index);
  };

  private setOverviewNode = (node: HTMLDivElement | null) => {
    if (!node) {
      return;
    }
    this.overviewNode = node;
    this.scrollWithOffsetX(0);
  };

  private setSlideNode = (node: HTMLDivElement | null) => {
    // node === null for components after unmount
    // more details https://github.com/yannickcr/eslint-plugin-react/issues/678#issuecomment-244566623
    if (!node) {
      return;
    }
    this.slideNode = node;
    this.smoothScrollToCurrentSlide();
  };

  private getScrollWithoutOffset() {
    return this.slideNode.offsetLeft + this.slideNode.clientWidth / 2 - window.innerWidth / 2;
  }

  private normalizeOffset(offsetX: number) {
    return (offsetX * this.slideNode.clientWidth) / window.innerWidth;
  }

  private scrollWithOffsetX(offsetX: number) {
    if (!this.overviewNode || !this.slideNode) {
      return;
    }
    this.overviewNode.scrollLeft = this.getScrollWithoutOffset() + this.normalizeOffset(offsetX);
  }

  private smoothScrollToCurrentSlide() {
    if (!window.requestAnimationFrame) {
      this.scrollWithOffsetX(0);

      return;
    }

    const { slideNode, overviewNode, props } = this;
    if (!overviewNode || !slideNode) {
      return;
    }

    const scrollWithoutOffset = this.getScrollWithoutOffset();
    const startOffsetX = props.offsetX;
    const pathLength = overviewNode.scrollLeft - scrollWithoutOffset;
    let startTime: number;

    function scroll(time: number) {
      // Останавливаем анимацию при свайпе
      if (startOffsetX !== props.offsetX) {
        return;
      }

      if (!startTime) {
        startTime = time;
        requestAnimationFrame(scroll);

        return;
      }

      const done = (time - startTime) / 200;

      if (done < 1) {
        overviewNode.scrollLeft = scrollWithoutOffset + pathLength * (1 - done);
        requestAnimationFrame(scroll);
      } else {
        overviewNode.scrollLeft = scrollWithoutOffset;
      }
    }
    requestAnimationFrame(scroll);
  }
}
